import { removeAccessToken } from '@app/utils/accessTokenStorage';
import { defineAccessTokenName } from '@app/utils/defineAccessTokenName';

/* eslint-disable no-console */
export type ClearCacheOptions = {
  clearSessionStorage?: boolean;
  clearIndexedDB?: boolean | string[];
  clearCacheStorage?: boolean;
  unregisterServiceWorkers?: boolean;
  reload?: boolean;
  cacheBusterParam?: string;
  localStorageKeys?: string[];
};

const DEFAULT_LOCAL_STORAGE_KEYS: string[] = [
  'NewUserAccessRights',
  'persist:root',
  'release/28-08/filters',
  'socialIntegrationsStatus',
  'sound-mode',
];

export const clearSessionStorageAndReload = async (
  opts: ClearCacheOptions = {}
): Promise<void> => {
  const accessTokenName = defineAccessTokenName(window.location.host);
  const {
    clearSessionStorage = true,
    clearIndexedDB = false,
    clearCacheStorage = true,
    unregisterServiceWorkers = true,
    reload = true,
    cacheBusterParam = '_',
    localStorageKeys,
  } = opts;

  const tasks: Promise<unknown>[] = [];

  if (clearCacheStorage && 'caches' in window) {
    tasks.push(
      (async () => {
        try {
          const names = await caches.keys();
          await Promise.all(names.map((n) => caches.delete(n)));
        } catch (error) {
          console.error('Error clearing Cache Storage', error);
        }
      })()
    );
  }

  if (unregisterServiceWorkers && 'serviceWorker' in navigator) {
    tasks.push(
      (async () => {
        try {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.unregister()));
        } catch (error) {
          console.error('Error unregistering Service Workers', error);
        }
      })()
    );
  }

  if (clearIndexedDB) {
    tasks.push(
      (async () => {
        try {
          if (Array.isArray(clearIndexedDB)) {
            await Promise.all(
              clearIndexedDB.map(
                (dbName) =>
                  new Promise<void>((resolve) => {
                    const req = indexedDB.deleteDatabase(dbName);
                    req.onsuccess =
                      req.onerror =
                      req.onblocked =
                        () => resolve();
                  })
              )
            );
          } else {
            const dbs: Array<{ name?: string }> =
              (await indexedDB.databases?.()) ?? [];
            await Promise.all(
              dbs
                .map((d) => d.name)
                .filter((n): n is string => !!n)
                .map(
                  (name) =>
                    new Promise<void>((resolve) => {
                      const req = indexedDB.deleteDatabase(name);
                      req.onsuccess =
                        req.onerror =
                        req.onblocked =
                          () => resolve();
                    })
                )
            );
          }
        } catch (error) {
          console.error('Error clearing IndexedDB', error);
        }
      })()
    );
  }

  // Токен живёт в sessionStorage, но сбрасывается всегда — даже когда полная
  // очистка сессии отключена опцией.
  removeAccessToken();

  // Полный сброс sessionStorage
  if (clearSessionStorage) {
    try {
      window.sessionStorage.clear();
    } catch (error) {
      console.error('Failed to clear sessionStorage', error);
    }
  }
  try {
    const keysToClear = [
      ...(localStorageKeys ?? DEFAULT_LOCAL_STORAGE_KEYS),
      accessTokenName,
    ];
    keysToClear.forEach((key) => {
      window.localStorage.removeItem(key);
    });
  } catch (error) {
    console.error('Failed to clear localStorage keys', error);
  }

  await Promise.race([
    Promise.allSettled(tasks),
    new Promise((r) => setTimeout(r, 1500)),
  ]);

  if (reload) {
    const url = new URL(window.location.href);
    url.searchParams.set(cacheBusterParam, Date.now().toString());
    window.location.replace(url.toString());
  }
};
